<template>
  <div class="max-w-6xl p-6 mx-auto md:p-8">
    <!-- Page header -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h2 class="text-2xl font-semibold text-gray-800">System Settings</h2>
        <p class="text-sm text-gray-500">
          Configure platform-wide behaviour for drivers, trips, incentives and
          apps
        </p>
      </div>
      <nav class="flex flex-wrap gap-2">
        <button
          v-for="group in groups"
          :key="group.id"
          type="button"
          @click="scrollToGroup(group.id)"
          class="inline-flex items-center h-9 px-3 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl shadow-sm transition-colors duration-150 hover:bg-taxiye-50 hover:text-taxiye-600"
        >
          <span class="mr-1.5 text-base material-icons-round">
            {{ group.icon }}
          </span>
          {{ group.label }}
        </button>
      </nav>
    </div>

    <form @submit.prevent="update" class="mt-6 space-y-4">
      <!-- ============================================================ -->
      <!-- GROUP: OPERATIONS                                            -->
      <!-- ============================================================ -->
      <div ref="operations" class="flex items-center pt-2 space-x-2 text-gray-500">
        <span class="text-base material-icons-round">local_taxi</span>
        <span class="text-xs font-semibold tracking-widest uppercase">
          Operations
        </span>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>

      <!-- Driver Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            badge
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Driver Settings
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to drivers
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Request Timeout (seconds)
            </span>
            <input
              v-model="settings.requestTimeout"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="30"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Default Commission
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.defaultCommission"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Default Road Pickup Commission
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.defaultRoadPickupCommission"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Default Rent Commission
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.rentCommission"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Trip Search Radius (km)
            </span>
            <input
              v-model="settings.searchRadius"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="30"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Rent Search Radius (km)
            </span>
            <input
              v-model="settings.rentSearchRadius"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="4"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Scheduled Trip Search Radius (km)
            </span>
            <input
              v-model="settings.scheduleSearchRadius"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="4"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Number of Drivers for Bid
            </span>
            <input
              v-model="settings.bidDriversPerRequest"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="4"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Medium Worker Average Trip (per day)
            </span>
            <input
              v-model="settings.mediumWorkerAverageTrip"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="4"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Hard Worker Average Trip (per day)
            </span>
            <input
              v-model="settings.hardWorkerAverageTrip"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="4"
            />
          </label>
        </div>
      </section>

      <!-- Driver Subscription Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            card_membership
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Driver Subscription
            </h3>
            <p class="text-sm text-gray-500">
              Drivers who subscribe pay this recurring fee and are charged 0%
              commission while their subscription is active
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label
            class="flex items-start justify-between px-4 py-3 border border-gray-200 cursor-pointer rounded-xl md:col-span-2 hover:border-taxiye-300"
          >
            <div class="pr-4">
              <span class="text-sm font-semibold text-gray-800">
                Enable Subscriptions
              </span>
              <p class="mt-0.5 text-xs text-gray-500">
                Master switch &mdash; when off, drivers get "subscription is
                not enabled" on all subscribe attempts
              </p>
            </div>
            <input
              v-model="settings.subscriptionSettings.enabled"
              type="checkbox"
              class="w-5 h-5 mt-0.5 text-taxiye-500 form-checkbox"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Amount
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.subscriptionSettings.amount"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="100"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Duration (days)
            </span>
            <input
              v-model="settings.subscriptionSettings.durationDays"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="30"
            />
          </label>
        </div>
      </section>

      <!-- Trip Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            route
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Trip Settings
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to rides/trips
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Cancel Cost
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.cancelCost"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Discount
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.discount"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Tax
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                %
              </span>
              <input
                v-model="settings.tax"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              SOS Timeout (days)
            </span>
            <input
              v-model="settings.sosDayLimit"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="20"
            />
          </label>
          <div class="md:col-span-2">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Surge Price Application Time
            </span>
            <div class="flex gap-2 mt-1.5">
              <time-picker
                placeholder="From"
                v-model="settings.surgeTimeFrom"
              ></time-picker>
              <time-picker
                placeholder="Upto"
                v-model="settings.surgeTimeUpto"
              ></time-picker>
            </div>
          </div>
        </div>
      </section>

      <!-- GPS Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            gps_fixed
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">GPS Settings</h3>
            <p class="text-sm text-gray-500">
              Set the settings related to GPS
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              General GPS Accuracy
            </span>
            <input
              v-model="settings.generalGpsAccuracy"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              In-trip GPS Accuracy
            </span>
            <input
              v-model="settings.onTripGpsAccuracy"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
        </div>
      </section>

      <!-- Delivery System Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            local_shipping
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Delivery System
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to the delivery system
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block md:col-span-2">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Verification Endpoint
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                API
              </span>
              <input
                v-model="settings.deliverySettings.deliveryVerificationEndpoint"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="http://"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Vehicle Type
            </span>
            <select
              @change="setVehicleType"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
            >
              <option value disabled selected hidden>
                Select a vehicle type...
              </option>
              <option
                v-for="type in vehicleTypes"
                :key="type._id"
                :value="type._id"
                :selected="
                  settings.deliverySettings.deliveryVehicleType === type._id
                "
              >
                {{ type.name }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- GROUP: INCENTIVES & PROMOTIONS                               -->
      <!-- ============================================================ -->
      <div
        ref="incentives"
        class="flex items-center pt-4 space-x-2 text-gray-500"
      >
        <span class="text-base material-icons-round">redeem</span>
        <span class="text-xs font-semibold tracking-widest uppercase">
          Incentives &amp; Promotions
        </span>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>

      <!-- First Trip Incentive -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            emoji_events
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              First Trip Incentive
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to first time trip incentive
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label
            class="flex items-start justify-between px-4 py-3 border border-gray-200 cursor-pointer rounded-xl hover:border-taxiye-300"
          >
            <div class="pr-4">
              <span class="text-sm font-semibold text-gray-800">Enable</span>
              <p class="mt-0.5 text-xs text-gray-500">
                Reward passengers on their first completed trip
              </p>
            </div>
            <input
              v-model="settings.applyFirstTripIncentive"
              type="checkbox"
              class="w-5 h-5 mt-0.5 text-taxiye-500 form-checkbox"
            />
          </label>
          <label class="block" v-if="settings.applyFirstTripIncentive">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Amount
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.firstTripIncentiveAmount"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="100"
              />
            </div>
          </label>
        </div>
      </section>

      <!-- CashBack / Incentive Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            savings
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              CashBack / Incentive
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to incentive
            </p>
          </div>
        </header>
        <div class="p-6">
          <div class="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Every "x" Trip
              </span>
              <input
                v-model="newIncentive.every"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Rate
              </span>
              <div class="flex mt-1.5 shadow-sm rounded-xl">
                <span
                  class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
                >
                  %
                </span>
                <input
                  v-model="newIncentive.rate"
                  class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                  placeholder="20"
                />
              </div>
            </label>
          </div>
          <div class="flex justify-end mt-4">
            <button
              type="button"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold rounded-xl shadow-sm transition-colors duration-150"
              :class="
                addIncentiveDisabled
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-white bg-taxiye-500 hover:bg-taxiye-600'
              "
              :disabled="addIncentiveDisabled"
              @click="addIncentive"
            >
              <span class="mr-1 text-base material-icons-round">add</span>
              Add
            </button>
          </div>

          <div class="mt-4 space-y-2" v-if="(settings.incentiveSettings || []).length">
            <div
              v-for="(incentiveSetting, i) in settings.incentiveSettings"
              :key="`incentive-${i}`"
              class="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl bg-gray-50"
            >
              <div class="flex items-center text-sm text-gray-700">
                <span
                  class="flex items-center justify-center w-8 h-8 mr-3 text-taxiye-500 bg-taxiye-50 rounded-lg material-icons-round text-base"
                >
                  loyalty
                </span>
                <span>
                  Every
                  <span class="font-semibold">{{ incentiveSetting.every }}</span>
                  trips &mdash;
                  <span class="font-semibold">{{ incentiveSetting.rate }}%</span>
                  cashback
                </span>
              </div>
              <button
                type="button"
                title="Remove"
                class="flex items-center justify-center w-8 h-8 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-red-100 hover:text-red-600 focus:outline-none"
                @click="removeIncentive(i)"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
          <p v-else class="mt-4 text-sm text-center text-gray-400">
            No cashback rules added yet.
          </p>
        </div>
      </section>

      <!-- Invitation Promo Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            group_add
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Invitation Promo
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to invitation based promo
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Daily Invitation Quota
            </span>
            <input
              v-model="settings.promoInvitationDailyQuota"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="10"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Promo Applies for "x" Trips
            </span>
            <input
              v-model="settings.promoNumberOfTripsApplicable"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="5"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Promo Incentive Amount
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                Br.
              </span>
              <input
                v-model="settings.promoIncentiveAmount"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="10"
              />
            </div>
          </label>
        </div>
      </section>

      <!-- Promo Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            local_offer
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Promo Settings
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to promo
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Amount
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                ETB
              </span>
              <input
                v-model="settings.promoAmount"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Trip Count
            </span>
            <input
              v-model="settings.promoTripCount"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="20"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Rate
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                %
              </span>
              <input
                v-model="settings.promoRate"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="20"
              />
            </div>
          </label>
        </div>
      </section>

      <!-- Incentive Voucher Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            confirmation_number
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Incentive Voucher
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to incentive voucher cashout
            </p>
          </div>
        </header>
        <div class="p-6">
          <div class="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
            <label class="block md:col-span-2">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Local IP
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.localIpAddress"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="local ip used in VPN"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Caller ID
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.callerId"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="caller Id"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Endpoint
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.endpoint"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Endpoint"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Encrypted
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.encrypted"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="encrypted"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Short Code
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.shortCode"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="short code"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Operator ID
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.operatorId"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="operator ID"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Pin
              </span>
              <input
                v-model="settings.telebirrAirTimeSettings.pin"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                :placeholder="
                  settings.telebirrAirTimeSettings.pinIsSet
                    ? '<pin hidden>'
                    : '<pin not set>'
                "
              />
            </label>
          </div>

          <!-- Voucher types -->
          <div class="pt-5 mt-6 border-t border-gray-100">
            <div class="flex items-center space-x-2 text-gray-500">
              <span class="text-base material-icons-round">sell</span>
              <span class="text-xs font-semibold tracking-widest uppercase">
                Voucher Types
              </span>
            </div>
            <div class="grid grid-cols-1 mt-4 gap-x-4 gap-y-5 md:grid-cols-2">
              <label class="block">
                <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Amount
                </span>
                <input
                  v-model="newVoucherType.amount"
                  class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="20"
                />
              </label>
              <label class="block">
                <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  ID
                </span>
                <input
                  v-model="newVoucherType.id"
                  class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="20"
                />
              </label>
            </div>
            <div class="flex justify-end mt-4">
              <button
                type="button"
                class="inline-flex items-center h-10 px-4 text-sm font-semibold rounded-xl shadow-sm transition-colors duration-150"
                :class="
                  addVoucherTypeDisabled
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-white bg-taxiye-500 hover:bg-taxiye-600'
                "
                :disabled="addVoucherTypeDisabled"
                @click="addVoucherType"
              >
                <span class="mr-1 text-base material-icons-round">add</span>
                Add
              </button>
            </div>

            <div
              class="mt-4 space-y-2"
              v-if="
                ((settings.telebirrAirTimeSettings || {}).voucherTypes || [])
                  .length
              "
            >
              <div
                v-for="(voucherType, i) in settings.telebirrAirTimeSettings
                  .voucherTypes"
                :key="`voucher-type-${i}`"
                class="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl bg-gray-50"
              >
                <div class="flex items-center text-sm text-gray-700">
                  <span
                    class="flex items-center justify-center w-8 h-8 mr-3 text-taxiye-500 bg-taxiye-50 rounded-lg material-icons-round text-base"
                  >
                    confirmation_number
                  </span>
                  <span>
                    ID
                    <span class="font-semibold">{{ voucherType.id }}</span>
                    &mdash;
                    <span class="font-semibold">
                      {{ voucherType.amount }} ETB
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  title="Remove"
                  class="flex items-center justify-center w-8 h-8 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-red-100 hover:text-red-600 focus:outline-none"
                  @click="removeVoucherType(i)"
                >
                  <span class="text-lg material-icons-round">close</span>
                </button>
              </div>
            </div>
            <p v-else class="mt-4 text-sm text-center text-gray-400">
              No voucher types added yet.
            </p>
          </div>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- GROUP: COMMUNICATION                                         -->
      <!-- ============================================================ -->
      <div
        ref="communication"
        class="flex items-center pt-4 space-x-2 text-gray-500"
      >
        <span class="text-base material-icons-round">forum</span>
        <span class="text-xs font-semibold tracking-widest uppercase">
          Communication
        </span>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>

      <!-- SMS Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            sms
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">SMS Settings</h3>
            <p class="text-sm text-gray-500">
              Set the settings related to SMS messages
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block md:col-span-2">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Endpoint
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-xs font-semibold text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                API
              </span>
              <input
                v-model="settings.smsSettings.endpoint"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="http://"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Username
            </span>
            <input
              v-model="settings.smsSettings.username"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Password
            </span>
            <input
              v-model="settings.smsSettings.password"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              DLR Mask
            </span>
            <input
              v-model="settings.smsSettings.dlrMask"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              DLR URL
            </span>
            <input
              v-model="settings.smsSettings.dlrURL"
              class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </label>
        </div>
      </section>

      <!-- Social Media Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            share
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">Social Media</h3>
            <p class="text-sm text-gray-500">
              Set the settings related to social medias
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Facebook Link
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">facebook</i>
              </span>
              <input
                v-model="settings.facebookLink"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="https://facebook.com/my-company"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Twitter Link
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <svg
                  class="w-4 h-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M17.3 6.2v.5c0 5-3.8 10.7-10.7 10.7-2.1 0-4.1-.6-5.8-1.7.3 0 .6.1.9.1 1.8 0 3.4-.6 4.7-1.6-1.7 0-3-1.1-3.5-2.6.2 0 .5.1.7.1.3 0 .7 0 1-.1-1.8-.4-3.1-1.9-3.1-3.8.5.3 1.1.5 1.7.5-1-.7-1.7-1.8-1.7-3.1 0-.7.2-1.3.5-1.9C3.9 5.5 6.7 7 9.9 7.2c-.1-.3-.1-.6-.1-.9 0-2.1 1.7-3.8 3.8-3.8 1.1 0 2.1.5 2.8 1.2.9-.2 1.7-.5 2.4-.9-.3.9-.9 1.6-1.7 2.1.8-.1 1.5-.3 2.2-.6-.6.8-1.2 1.4-2 1.9z"
                  />
                </svg>
              </span>
              <input
                v-model="settings.twitterLink"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="https://twitter.com/@myCompany"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Google Link
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <svg
                  class="w-4 h-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M2 5.6c0 1.5.5 2.6 1.5 3.2.8.5 1.7.6 2.2.6H6s-.2 1 .6 2h-.1c-1.3 0-5.5.3-5.5 3.7 0 3.5 3.9 3.7 4.6 3.7H5.9c.5 0 1.8-.1 3-.6 1.5-.7 2.3-2.1 2.3-3.9 0-1.8-1.2-2.8-2.1-3.6-.5-.5-1-.9-1-1.3s.3-.7.8-1.1c.7-.6 1.3-1.5 1.3-3.1 0-1.5-.2-2.4-1.4-3.1.1-.1.6-.1.8-.1.6-.1 1.6-.2 1.6-.7v-.1H6.6S2 1.4 2 5.6zm7.4 9c.1 1.4-1.1 2.4-2.9 2.6-1.8.1-3.3-.7-3.4-2.1 0-.7.3-1.3.8-1.9.6-.5 1.4-.9 2.3-.9h.3c1.7 0 2.8 1 2.9 2.3zm-1.2-10c.5 1.6-.2 3.2-1.3 3.6h-.4c-1 0-2-1-2.3-2.4-.2-.7-.2-1.4 0-2.1.2-.6.6-1.1 1.2-1.2.1 0 .3-.1.4-.1 1.2 0 1.9.5 2.4 2.2zM16 8V5h-2v3h-3v2h3v3h2v-3h3V8h-3z"
                  />
                </svg>
              </span>
              <input
                v-model="settings.googleLink"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="https://plus.google.com/my-company"
              />
            </div>
          </label>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- GROUP: COMPANY & APPS                                        -->
      <!-- ============================================================ -->
      <div ref="company" class="flex items-center pt-4 space-x-2 text-gray-500">
        <span class="text-base material-icons-round">business</span>
        <span class="text-xs font-semibold tracking-widest uppercase">
          Company &amp; Apps
        </span>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>

      <!-- Company Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            apartment
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Company Settings
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to the company
            </p>
          </div>
        </header>
        <div class="grid grid-cols-1 p-6 gap-x-4 gap-y-5 md:grid-cols-2">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Contact Number
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">call</i>
              </span>
              <input
                v-model="settings.contactNumber"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="+251 999 999999"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Contact Email
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">email</i>
              </span>
              <input
                v-model="settings.contactEmail"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="company@domain.com"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Credit Allowance
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">attach_money</i>
              </span>
              <input
                v-model="settings.creditAllowance"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="4000"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Map Key
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">vpn_key</i>
              </span>
              <input
                v-model="settings.mapKey"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="4000"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Logo Image
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">photo</i>
              </span>
              <input
                v-model="settings.logo"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="Image"
              />
            </div>
          </label>
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Fav Icon
            </span>
            <div class="flex mt-1.5 shadow-sm rounded-xl">
              <span
                class="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl"
              >
                <i class="text-lg material-icons-round">photo</i>
              </span>
              <input
                v-model="settings.favIcon"
                class="block w-full h-10 text-sm text-gray-800 border-gray-300 form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
                placeholder="Fav Icon"
              />
            </div>
          </label>
        </div>
      </section>

      <!-- App Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            smartphone
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">App Settings</h3>
            <p class="text-sm text-gray-500">
              Set the settings related app version
            </p>
          </div>
        </header>
        <div class="p-6">
          <!-- Driver app -->
          <div class="flex items-center space-x-2 text-gray-500">
            <span class="text-base material-icons-round">directions_car</span>
            <span class="text-xs font-semibold tracking-widest uppercase">
              Driver App
            </span>
          </div>
          <div class="grid grid-cols-1 mt-4 gap-x-4 gap-y-5 md:grid-cols-2">
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Latest Android Version
              </span>
              <input
                v-model="settings.androidDriverVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Least Supported Android Version
              </span>
              <input
                v-model="settings.leastAndroidDriverVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Latest iOS Version
              </span>
              <input
                v-model="settings.iosDriverVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Least Supported iOS Version
              </span>
              <input
                v-model="settings.leastIosDriverVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Appstore Link
              </span>
              <input
                v-model="settings.driverAppstoreLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://www.apple.com/app-store/"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Playstore Link
              </span>
              <input
                v-model="settings.driverPlaystoreLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://play.google.com/store"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Privacy Policy Link
              </span>
              <input
                v-model="settings.driverPrivacyPolicyLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://..."
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Terms and Conditions Link
              </span>
              <input
                v-model="settings.driverTermsAndConditionLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://..."
              />
            </label>
          </div>

          <!-- Passenger app -->
          <div
            class="flex items-center pt-5 mt-6 space-x-2 text-gray-500 border-t border-gray-100"
          >
            <span class="text-base material-icons-round">person</span>
            <span class="text-xs font-semibold tracking-widest uppercase">
              Passenger App
            </span>
          </div>
          <div class="grid grid-cols-1 mt-4 gap-x-4 gap-y-5 md:grid-cols-2">
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Latest Android Version
              </span>
              <input
                v-model="settings.androidPassengerVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Least Supported Android Version
              </span>
              <input
                v-model="settings.leastAndroidPassengerVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Latest iOS Version
              </span>
              <input
                v-model="settings.iosPassengerVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Least Supported iOS Version
              </span>
              <input
                v-model="settings.leastIosPassengerVersion"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="0.0.1"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Appstore Link
              </span>
              <input
                v-model="settings.passengerAppstoreLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://www.apple.com/app-store/"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Playstore Link
              </span>
              <input
                v-model="settings.passengerPlaystoreLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://play.google.com/store"
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Privacy Policy Link
              </span>
              <input
                v-model="settings.passengerPrivacyPolicyLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://..."
              />
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Terms and Conditions Link
              </span>
              <input
                v-model="settings.passengerTermsAndConditionLink"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="https://..."
              />
            </label>
          </div>
        </div>
      </section>

      <!-- Device Ban Settings -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            phonelink_erase
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Device Ban Settings
            </h3>
            <p class="text-sm text-gray-500">
              Set the settings related to unsupported devices restriction
            </p>
          </div>
        </header>
        <div class="p-6">
          <label class="block">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Message for Unknown/Unsupported Devices
            </span>
            <textarea
              v-model="settings.deviceUnknownNote"
              rows="3"
              class="block w-full mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-textarea rounded-xl focus:border-taxiye-300"
            ></textarea>
          </label>
        </div>
      </section>

      <!-- Save bar -->
      <div
        class="sticky z-10 flex items-center justify-between px-6 py-4 bg-white border border-gray-200 rounded-2xl shadow-lg bottom-4"
      >
        <p class="text-sm text-gray-500">
          Changes apply system-wide once saved.
        </p>
        <button
          type="submit"
          class="inline-flex items-center h-10 px-8 text-sm font-semibold text-white rounded-xl shadow-sm transition-colors duration-150"
          :disabled="busy"
          :class="
            busy
              ? 'bg-taxiye-300 cursor-not-allowed'
              : 'bg-taxiye-500 hover:bg-taxiye-600'
          "
        >
          <span class="mr-1.5 text-base material-icons-round">save</span>
          {{ busy ? "Saving..." : "Save Changes" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import TimePicker from "../../components/TimePicker.vue";

export default {
  data() {
    return {
      settings: {
        telebirrAirTimeSettings: {},
        smsSettings: {},
        deliverySettings: {},
        subscriptionSettings: { amount: 100, durationDays: 30, enabled: false },
      },
      busy: false,
      newIncentive: {
        rate: 0,
        every: 0,
      },
      newVoucherType: {
        amount: 0,
        id: 0,
      },
      vehicleTypes: [],
      loadingVehicleTypes: false,
      groups: [
        { id: "operations", label: "Operations", icon: "local_taxi" },
        { id: "incentives", label: "Incentives & Promos", icon: "redeem" },
        { id: "communication", label: "Communication", icon: "forum" },
        { id: "company", label: "Company & Apps", icon: "business" },
      ],
    };
  },
  components: {
    TimePicker,
  },
  created() {
    this.$misc
      .get("/setting")
      .then(response => {
        this.settings = response.data;
        if (!this.settings.deliverySettings)
          this.settings.deliverySettings = {};
        if (!this.settings.subscriptionSettings)
          this.settings.subscriptionSettings = {
            amount: 100,
            durationDays: 30,
            enabled: false,
          };
      })
      .catch(err => console.log(err));
    this.getVehicleTypes();
  },
  computed: {
    addIncentiveDisabled() {
      return [
        !this.newIncentive.rate,
        !this.newIncentive.every,
        isNaN(this.newIncentive.rate),
        isNaN(this.newIncentive.every),
        this.newIncentive.rate > 100,
        this.newIncentive.rate <= 0,
        this.newIncentive.every != parseInt(this.newIncentive.every),
        this.newIncentive.every < 1,
      ].some(x => x);
    },
    addVoucherTypeDisabled() {
      return [
        !this.newVoucherType.amount,
        !this.newVoucherType.id,
        isNaN(this.newVoucherType.amount),
        this.newVoucherType.amount < 0,
        parseInt(this.newVoucherType.amount) !=
          Number(this.newVoucherType.amount),
      ].some(x => x);
    },
  },
  methods: {
    update() {
      this.busy = true;
      this.$misc
        .post("/setting", { ...this.settings })
        .then(() => {
          window.flash({ type: "success", body: "Updated Successfully!" });
        })
        .catch(err => {
          window.flash({ type: "error", body: "Failed to save settings!" });
          console.log(err);
        })
        .finally(() => (this.busy = false));
    },
    addIncentive() {
      if (!this.settings.incentiveSettings) this.settings.incentiveSettings = [];
      this.settings.incentiveSettings.push({ ...this.newIncentive });
      this.newIncentive = { rate: 0, every: 0 };
      this.update();
    },
    removeIncentive(index) {
      this.settings.incentiveSettings.splice(index, 1);
      this.update();
    },
    addVoucherType() {
      if (!this.settings.telebirrAirTimeSettings.voucherTypes)
        this.$set(this.settings.telebirrAirTimeSettings, "voucherTypes", []);
      this.settings.telebirrAirTimeSettings.voucherTypes.push({
        ...this.newVoucherType,
      });
      this.newVoucherType = { amount: 0, id: 0 };
      this.update();
    },
    removeVoucherType(index) {
      this.settings.telebirrAirTimeSettings.voucherTypes.splice(index, 1);
      this.update();
    },
    getVehicleTypes() {
      this.loadingVehicleTypes = true;
      this.$misc
        .get("vehicleTypes")
        .then(response => {
          this.vehicleTypes = response.data.data;
        })
        .catch(err => console.log(err))
        .finally(() => (this.loadingVehicleTypes = false));
    },
    setVehicleType(e) {
      if (e.target.value.trim() == "") {
        return;
      }
      this.settings.deliverySettings.deliveryVehicleType = e.target.value;
    },
  },
};
</script>
